import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  // YAxis,
} from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../config/enum';
import currentTimeZone from '../../../config/timeZone';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import { CapitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import dashboard from '../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Station alert chart component
 * @returns
 */
export default function StationAlertsChart() {
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  const [barChartLabel, setBarChartLabel] = useState([
    { name: 'maintenance', value: 0, color: '#0088FE', isActive: true },
    { name: 'transaction', value: 0, color: '#00C49F', isActive: true },
    // { name: 'energy', value: 0, color: '#FFBB28', isActive: true },
    { name: 'performance', value: 0, color: '#FF8042', isActive: true },
  ]);

  /**
   * Show & hide data in bar-chart label
   */
  const handleToggleLabelActive = (name: string, value: boolean) => {
    setBarChartLabel((previousValue) =>
      previousValue.map((item) => (item.name === name ? { ...item, isActive: value } : item)),
    );
  };

  /**
   * Update all alerts totals
   */
  const updateAllAlertTotals = (data: any[]) => {
    // Initialize totals
    const totals = {
      maintenance: 0,
      transaction: 0,
      performance: 0,
    };

    // Accumulate totals
    data.forEach((item) => {
      totals.maintenance += item.maintenanceAlert || 0;
      totals.transaction += item.transactionAlert || 0;
      totals.performance += item.performanceAlert || 0;
    });

    // Update state
    setBarChartLabel((previousData) =>
      previousData.map((label) => ({
        ...label,
        value: totals[label.name as keyof typeof totals] || 0,
      })),
    );
  };

  /**
   * Function to show custom tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="station-performance-custom-tooltip">
          <p className="label">{data?.payload?.stationName}</p>
          <div className="tooltip-values">
            <p style={{ color: '#0088FE' }}>Maintenance: {data?.payload?.maintenance}</p>
            <p style={{ color: '#00C49F' }}>Transaction: {data?.payload?.transaction}</p>
            <p style={{ color: '#FF8042' }}>Performance: {data?.payload?.performance}</p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await dashboard.getAlertsReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      updateAllAlertTotals(response?.data);

      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last12months');
  }, []);

  return (
    <div className="station-alert-chart-wrap">
      <div className="station-alert-chart-header">
        <div className="header-title">
          <h6>Station Alert</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/alerts/all')}>
            <NavigateArrow />
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => duration.label)}
          onToggle={(index, label) =>
            fetchPowerConsumedReport(
              chartTimePeriod.find((duration) => duration.label === label)?.value || 'last12months',
            )
          }
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      <div className="station-alert-chart-container">
        <ResponsiveContainer width="100%" height={150}>
          <BarChart
            data={barGraphData}
            margin={{
              top: 50,
              right: 0,
              left: -10,
              bottom: 5,
            }}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
            {/* <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} /> */}
            {barChartLabel
              .filter((label) => label.isActive)
              .map((label) => (
                <Bar
                  key={label.name}
                  dataKey={`${label.name}Alert`} // e.g., 'performanceAlert'
                  fill={label.color}
                />
              ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="station-alert-chart-label">
        {barChartLabel.map((eachData) => (
          <div className="each-label-container">
            <div
              className="each-label"
              style={{ '--label-color': eachData.color } as React.CSSProperties}>
              <p>{CapitalizeFirstLetter(eachData.name)}</p>
              <div className="each-label-value">
                <span>{eachData.value}</span>
              </div>
            </div>
            <CustomInputSwitch
              name={eachData.name}
              value={eachData.isActive}
              onChangeValue={handleToggleLabelActive}
              fontSize="small"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
